import axios, {AxiosResponse} from 'axios';
import {CatalogResponse, Documents, EfeResponse, Layout} from 'commons/models';
import {USER_DATA} from 'commons/constants';
import {Utils} from 'commons';
import {onTime} from 'commons/utils/Functions';
import jwtDecode from 'jwt-decode';
import {
    REACT_APP_BASE_URL_USERS,
    REACT_APP_BASE_URL_LAYOUT,
    REACT_APP_BASE_URL_CATALOG,
    REACT_APP_BASE_URL_DOCUMENTS,
    REACT_APP_BASE_URL_CLIENTS,
    REACT_APP_BASE_URL_SECURITY,
    REACT_APP_BASE_URL_KEYCLOAK,
    REACT_APP_KEYCLOAK_REALM,
    REACT_APP_BASE_URL_VALIDATIONS,
    REACT_APP_BASE_URL_LOG,
    REACT_APP_BASE_URL_REPORT
} from 'commons/env/env-vars';

/**
 * Handle Error on rejected promises
 * @param error
 * @returns {Promise<never>}
 */

const handleError = function (error: any) {
    let errorResponse = {
        data: null,
        success: false,
        code: error.status,
        status: error.name,
        message: error.message,
    };

    if (error.response && error.response.data) {
        errorResponse.data = error.response.data;
    }
    if (error.request) {
        errorResponse = {
            ...errorResponse,
            code: error.request.status,
            status: 'Error',
        };
    }
    return Promise.reject(errorResponse);
};

const UsersInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_USERS}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);
    return instance;
};

/**
 * Layouts Instance
 * @constructor: Setup defaults: Instance & Interceptors
 */
const LayoutInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_LAYOUT}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse<EfeResponse<Layout>>) {
        return response;
    }, handleError);
    return instance;
};

/**
 * Catalogs Instance
 * @constructor: Setup defaults: Instance & Interceptors
 */
const CatalogInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_CATALOG}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse<CatalogResponse>) {
        return response;
    }, handleError);
    return instance;
};

const DocumentsInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_DOCUMENTS}`,
        responseType: 'arraybuffer',
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse<Documents>) {
        response.headers = {
            'Access-Control-Expose-Headers': 'Content-Disposition',
            'Content-Type': 'multipart/form-data',
        };
        return response;
    }, handleError);
    return instance;
};

const ClientsInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_CLIENTS}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`
            };

            if (request?.url?.startsWith('/api/v1/clients/enrollment/consignees/accounts?')) {
                const res = await axios.get('https://api.ipify.org/?format=json');
                request.headers = {
                    ...request.headers, 'X-Aml-AuditUser': USER_DATA.username,
                    'X-Aml-AuditDate': `${new Date().toISOString()}`,
                    'X-Aml-DeviceData': window.navigator.userAgent,
                    'X-Aml-IpAddress': res && res.data ? res.data.ip : '',
                };
            }
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);
    return instance;
};

const AmlSyncInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_CLIENTS}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            const res = await axios.get('https://api.ipify.org/?format=json');
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
                'X-Aml-AuditUser': USER_DATA.username,
                'X-Aml-AuditDate': `${new Date().toISOString()}`,
                'X-Aml-DeviceData': window.navigator.userAgent,
                'X-Aml-IpAddress': res && res.data ? res.data.ip : '',
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);
    return instance;
};

const SecurityInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_SECURITY}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);
    return instance;
};

const LogsInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_LOG}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);
    return instance;
};

const KeycloakInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_KEYCLOAK}${REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect`,
    });
    instance.interceptors.request.use(async (request) => {
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);
    return instance;
};

const ValidationsInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_VALIDATIONS}`,
    });
    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
            };
        }
        return request;
    }, handleError);

    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);
    return instance;
};

const ReportInstance = () => {
    const instance = axios.create({
        baseURL: `${REACT_APP_BASE_URL_REPORT}`,
        responseType: 'arraybuffer',
    });

    instance.interceptors.request.use(async (request) => {
        if (USER_DATA.token && !USER_DATA.logout && USER_DATA.loggedIn) {
            const token: any = jwtDecode(USER_DATA.token);
            if (!onTime(new Date(token.exp * 1000))) {
                await Utils.functions.refreshToken();
            }
            request.headers = {
                Authorization: `${USER_DATA.tokenType} ${USER_DATA.token}`,
            };
        }
        return request;
    }, handleError);
    instance.interceptors.response.use(function (response: AxiosResponse) {
        return response;
    }, handleError);

    return instance;
};

const EfeApi = {
    Users: UsersInstance(),
    Layouts: LayoutInstance(),
    Catalogs: CatalogInstance(),
    Documents: DocumentsInstance(),
    Clients: ClientsInstance(),
    Security: SecurityInstance(),
    Keycloak: KeycloakInstance(),
    Validations: ValidationsInstance(),
    AmlSync: AmlSyncInstance(),
    Logs: LogsInstance(),
    Reports: ReportInstance(),
};

export default EfeApi;
